import React, { useEffect } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
  Link,
  useSearchParams,
  Navigate,
  useNavigate,
} from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import LoginPage from './pages/LoginPage';
import AcceptInvitePage from './pages/AcceptInvitePage';
import {
  Home,
  LayoutGrid,
  Scale,
  MessageSquare,
  Search,
  Compass,
  SearchCheck,
  ClipboardCheck,
  ShieldCheck,
  Users,
  FileText,
  Wrench,
  MailPlus,
} from 'lucide-react';
// Classic / Ticket-System vorübergehend deaktiviert – nur Ressourcen-Portal aktiv.
// import InternalDashboard from './components/InternalDashboard';
// import TicketList from './components/TicketList';
// import TicketDetail from './components/TicketDetail';
// import CreateTicket from './components/CreateTicket';
// import ProjectList from './components/ProjectList';
// import ProjectDetail from './components/ProjectDetail';
// import Reporting from './components/Reporting';
// import WidgetConfiguration from './components/WidgetConfiguration';
// import Tools from './components/Tools';
import ResourcesDashboard from './components/resources/ResourcesDashboard';
import GehaltsrechnerResource from './components/resources/GehaltsrechnerResource';
import UrlaubsrechnerResource from './components/resources/UrlaubsrechnerResource';
import SelbstcheckComplianceResource from './components/resources/SelbstcheckComplianceResource';
import HrSelbstcheckResource from './components/resources/HrSelbstcheckResource';
import HrHandbookChatbotResource from './components/resources/HrHandbookChatbotResource';
import AskExpertResource from './components/resources/AskExpertResource';
import EntscheidungsassistentResource from './components/resources/EntscheidungsassistentResource';
import InviteCreateResource from './components/resources/InviteCreateResource';
import EntgelttransparenzResource from './components/resources/EntgelttransparenzResource';
import KiRecruitingResource from './components/resources/KiRecruitingResource';
import EntgeltSidebarPanel from './components/resources/EntgeltSidebarPanel';
import DecisionSidebarPanel from './components/resources/DecisionSidebarPanel';
import { EntgeltEmbedProvider, useEntgeltEmbed } from './context/EntgeltEmbedContext';
import { HandbookEmbedProvider } from './context/HandbookEmbedContext';
import { DecisionEmbedProvider } from './context/DecisionEmbedContext';
import logo from './assets/logo.png';
import { DEV_SKIP_LOGIN } from './config/devBypass';
import { isPublicResourcePath } from './config/resourceConfig';

/** Exakte URL /resources ohne Tool-Pfad → Dashboard (frühere „Übersicht“ entfällt). */
function ResourcesRootRedirect() {
  const { search } = useLocation();
  return <Navigate to={{ pathname: '/', search }} replace />;
}

function AppContent() {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { embedSidebarCollapsed, setEmbedSidebarCollapsed, postToEmbed } = useEntgeltEmbed();

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  const searchSuffix = searchParams.toString() ? `?${searchParams.toString()}` : '';
  const canCreateInvites = (user?.email || '').toLowerCase().endsWith('@hrfactory.com');
  const isGuestOnPublicTool = !user && isPublicResourcePath(location.pathname);
  const portalHomePath = user ? `/${searchSuffix}` : '/login';

  const isEntgeltRoute = location.pathname === '/resources/entgelttransparenz';
  const isDecisionRoute = location.pathname === '/resources/entscheidungs-assistent';
  const isHrSelbstcheckRoute = location.pathname === '/resources/hr-selbstcheck';
  const isHrHandbookChatbotRoute = location.pathname === '/resources/hr-self-service-chatbot';
  const isDashboardRoute = location.pathname === '/';
  const isFullBleedResource =
    isEntgeltRoute || isDecisionRoute || isHrSelbstcheckRoute || isHrHandbookChatbotRoute;
  // Hinweis: isHrHandbookChatbotRoute bleibt in isFullBleedResource (iframe braucht full height),
  // aber die Chatbot-Route nutzt keine eigene Sidebar-Sub-Navigation mehr.

  useEffect(() => {
    if (!isEntgeltRoute) setEmbedSidebarCollapsed(false);
  }, [isEntgeltRoute, setEmbedSidebarCollapsed]);

  // Unbekannte Pfade innerhalb des geschützten Bereichs → zurück zum Dashboard
  const pathOk = location.pathname === '/' || location.pathname.startsWith('/resources');
  if (!pathOk) {
    return <Navigate to="/" replace />;
  }


  const handleEntgeltMenuClick = (event) => {
    if (location.pathname !== '/resources/entgelttransparenz') return;
    event.preventDefault();
    postToEmbed?.({ type: 'et-host-overview' });
  };

  // ── Helper: sidebar nav link ──
  const sidebarLink = (to, icon, label, active) => (
    <Link
      to={to}
      className={[
        'flex items-center py-2.5 pr-3 text-sm font-medium rounded-lg transition-all duration-150',
        active
          ? 'border-l-2 border-hrf-coral pl-2.5 bg-white/10 text-white'
          : 'border-l-2 border-transparent pl-2.5 text-white/50 hover:bg-white/10 hover:text-white/80',
      ].join(' ')}
    >
      <span className="mr-3 shrink-0">{icon}</span>
      {label}
    </Link>
  );

  const sidebarMenuItem = (label, key, icon, to = null, active = false) => {
    const Icon = icon ?? LayoutGrid;
    if (to) {
      return (
        <Link
          key={key}
          to={to}
          className={[
            'flex items-center py-2 pr-3 text-sm font-medium rounded-lg transition-all duration-150',
            active
              ? 'border-l-2 border-hrf-coral pl-2.5 bg-white/10 text-white'
              : 'border-l-2 border-transparent pl-2.5 text-white/50 hover:bg-white/10 hover:text-white/80',
          ].join(' ')}
        >
          <span className="mr-3 shrink-0">
            <Icon className="h-4 w-4" />
          </span>
          {label}
        </Link>
      );
    }
    return (
        <div
        key={key}
        className="flex items-center py-2 pr-3 text-sm font-medium rounded-lg border-l-2 border-transparent pl-2.5 text-white/30"
      >
        <span className="mr-3 shrink-0">
          <Icon className="h-4 w-4" />
        </span>
        {label}
      </div>
    );
  };

  // Ressourcen-Portal (Haupt-UI)
  const resourcesEntAsideClass = isEntgeltRoute
    ? embedSidebarCollapsed
      ? 'w-14 min-w-[3.5rem]'
      : 'w-72 min-w-[18rem]'
    : isDecisionRoute
      ? 'w-72 min-w-[18rem]'
    : 'w-64 min-w-[16rem]';

  const resourcesShell = (
    <div className="min-h-screen flex" style={{ backgroundColor: '#f8fafc' }}>

      {/* ── Sidebar ── */}
      <aside
        className={[
          'h-screen sticky top-0 flex flex-col shrink-0 overflow-hidden transition-[width,min-width] duration-200 ease-out',
          resourcesEntAsideClass,
        ].join(' ')}
        style={{ backgroundColor: '#1e293b' }}
      >
        {/* Logo: im Entgelt-Modus nur Marke, kein Portal-Mix in der Sidebar */}
        <div
          className={
            isEntgeltRoute && embedSidebarCollapsed
              ? 'px-2 pt-4 pb-2 flex justify-center'
              : 'px-5 pt-5 pb-4'
          }
        >
          {isEntgeltRoute ? (
            <img
              src={logo}
              alt="HR Factory"
              className={
                embedSidebarCollapsed
                  ? 'h-6 w-auto max-w-[2rem] object-contain'
                  : 'h-8 w-auto'
              }
            />
          ) : (
            <Link to={portalHomePath} className="inline-block">
              <img src={logo} alt="HR Factory" className="h-8 w-auto" />
            </Link>
          )}
        </div>

        {!(isEntgeltRoute && embedSidebarCollapsed) && <div className="mx-5 h-px bg-white/10 mb-4" />}

        {/* Navigation: Route-spezifische Sidebar bei eingebetteten Tools */}
        {isEntgeltRoute ? (
          <div
            className={[
              'flex-1 flex flex-col min-h-0 overflow-hidden',
              embedSidebarCollapsed ? 'px-1' : 'px-3',
            ].join(' ')}
          >
            <EntgeltSidebarPanel variant="resources" dashboardPath={`/${searchSuffix}`} />
          </div>
        ) : isDecisionRoute ? (
          <div className="flex-1 flex flex-col min-h-0 overflow-hidden px-3">
            <DecisionSidebarPanel dashboardPath={portalHomePath} guestMode={isGuestOnPublicTool} />
          </div>
        ) : (
          <nav className="scrollbar-minimal flex-1 px-3 overflow-y-auto space-y-1">
            <p className="px-3 mb-2 text-[10px] font-semibold text-white/35 uppercase tracking-widest">
              Portal
            </p>

            {sidebarLink(
              `/${searchSuffix}`,
              <Home className="h-4 w-4" />,
              'Dashboard',
              location.pathname === '/',
            )}

            <div className="pt-4 pb-1">
              <p className="px-3 text-[10px] font-semibold text-white/35 uppercase tracking-widest">
                Entscheidungshilfen
              </p>
            </div>
            {sidebarMenuItem(
              'HR-Kompass',
              'entscheidungshilfen-hr-kompass',
              Compass,
              `/resources/entscheidungs-assistent${searchSuffix}`,
              location.pathname === '/resources/entscheidungs-assistent',
            )}
            {sidebarMenuItem(
              'Frag einen Expert',
              'entscheidungshilfen-frag-einen-expert',
              SearchCheck,
              `/resources/frag-einen-expert${searchSuffix}`,
              location.pathname === '/resources/frag-einen-expert',
            )}
            <Link
              to={{ pathname: '/resources/hr-selbstcheck', search: searchSuffix }}
              className={[
                'flex items-center py-2 pr-3 text-sm font-medium rounded-lg transition-all duration-150',
                location.pathname === '/resources/hr-selbstcheck'
                  ? 'border-l-2 border-hrf-coral pl-2.5 bg-white/10 text-white'
                  : 'border-l-2 border-transparent pl-2.5 text-white/50 hover:bg-white/10 hover:text-white/80',
              ].join(' ')}
            >
              <span className="mr-3 shrink-0">
                <ClipboardCheck className="h-4 w-4" />
              </span>
              HR Selbstcheck
            </Link>
            {sidebarMenuItem('KI Selbstcheck', 'entscheidungshilfen-ki-selbstcheck', ShieldCheck)}

            <div className="pt-4 pb-1">
              <p className="px-3 text-[10px] font-semibold text-white/35 uppercase tracking-widest">
                Werkzeuge
              </p>
            </div>
            <Link
              to={{ pathname: '/resources/entgelttransparenz', search: searchSuffix }}
              state={{ forceOverview: true }}
              onClick={handleEntgeltMenuClick}
              className={[
                'flex items-center py-2 pr-3 text-sm font-medium rounded-lg transition-all duration-150',
                location.pathname === '/resources/entgelttransparenz'
                  ? 'border-l-2 border-hrf-coral pl-2.5 bg-white/10 text-white'
                  : 'border-l-2 border-transparent pl-2.5 text-white/50 hover:bg-white/10 hover:text-white/80',
              ].join(' ')}
            >
              <span className="mr-3 shrink-0">
                <Scale className="h-4 w-4" />
              </span>
              Entgelttransparenz-Assistent
            </Link>
            {sidebarMenuItem(
              'HR Self-Service Chatbot',
              'werkzeuge-hr-self-service-chatbot',
              MessageSquare,
              `/resources/hr-self-service-chatbot${searchSuffix}`,
              location.pathname === '/resources/hr-self-service-chatbot',
            )}
            {sidebarMenuItem(
              'KI-Recruiting',
              'werkzeuge-ki-recruiting',
              Search,
              `/resources/ki-recruiting${searchSuffix}`,
              location.pathname === '/resources/ki-recruiting',
            )}

            <div className="pt-4 pb-1">
              <p className="px-3 text-[10px] font-semibold text-white/35 uppercase tracking-widest">
                Auf Anfrage
              </p>
            </div>
            {sidebarMenuItem('HR Experts Vorstellung', 'auf-anfrage-hr-experts-vorstellung', Users)}
            {sidebarMenuItem('Zeugnisgenerator', 'auf-anfrage-zeugnisgenerator', FileText)}
            {sidebarMenuItem('Maßgeschneiderte KI-Tools', 'auf-anfrage-massgeschneiderte-ki-tools', Wrench)}

            {canCreateInvites && (
              <>
                <div className="pt-4 pb-1">
                  <p className="px-3 text-[10px] font-semibold text-white/35 uppercase tracking-widest">
                    Intern
                  </p>
                </div>
                {sidebarMenuItem(
                  'Einladung erstellen',
                  'intern-einladung-erstellen',
                  MailPlus,
                  `/resources/einladung-erstellen${searchSuffix}`,
                  location.pathname === '/resources/einladung-erstellen',
                )}
              </>
            )}
          </nav>
        )}

        {/* Bottom: Nutzerkonto oder Gast-Anmeldung */}
        <div
          className={[
            'border-t border-white/10',
            isEntgeltRoute && embedSidebarCollapsed ? 'px-2 py-3 flex justify-center' : 'px-4 py-4',
          ].join(' ')}
          style={{ backgroundColor: 'rgba(255,255,255,0.04)' }}
        >
          {user ? (
            <div
              className={[
                'flex items-center gap-3',
                isEntgeltRoute && embedSidebarCollapsed ? 'justify-center' : '',
              ].join(' ')}
            >
              <div className="w-8 h-8 rounded-full bg-hrf-coral flex items-center justify-center text-[11px] font-bold text-white shrink-0 select-none">
                {(user.first_name?.[0] || user.email?.[0] || '?').toUpperCase()}
              </div>
              {!(isEntgeltRoute && embedSidebarCollapsed) && (
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-white leading-none mb-0.5 truncate">
                    {user.first_name && user.last_name
                      ? `${user.first_name} ${user.last_name}`
                      : user.email}
                  </p>
                  <button
                    type="button"
                    onClick={handleLogout}
                    className="text-xs text-white/40 hover:text-hrf-coral transition-colors"
                  >
                    Abmelden
                  </button>
                </div>
              )}
            </div>
          ) : (
            !(isEntgeltRoute && embedSidebarCollapsed) && (
              <div className="space-y-1">
                <Link
                  to="/login"
                  state={{ from: location }}
                  className="text-sm font-medium text-white hover:text-hrf-coral transition-colors"
                >
                  Anmelden
                </Link>
                <p className="text-xs text-white/40 leading-snug">
                  Für Dashboard und weitere Tools
                </p>
              </div>
            )
          )}
        </div>
      </aside>

      {/* ── Main area ── */}
      <div className="flex-1 flex flex-col min-h-screen">

        {/* Content */}
        <main
          className={
            isFullBleedResource
              ? 'flex-1 flex flex-col min-h-0 min-w-0 p-0 overflow-hidden'
              : isDashboardRoute
                ? 'flex-1 flex flex-col min-h-0 min-w-0 p-0'
                : 'flex-1 p-8'
          }
        >
          <div
            className={
              isFullBleedResource
                ? 'flex-1 flex flex-col min-h-0 min-w-0 w-full'
                : isDashboardRoute
                  ? 'flex-1 flex flex-col min-h-0 min-w-0 w-full'
                  : 'max-w-4xl mx-auto'
            }
          >
            <Routes>
              <Route path="/" element={<ResourcesDashboard />} />
              <Route path="/resources" element={<ResourcesRootRedirect />} />
              <Route path="/resources/gehaltsrechner" element={<GehaltsrechnerResource />} />
              <Route path="/resources/urlaubsrechner" element={<UrlaubsrechnerResource />} />
              <Route path="/resources/selbstcheck-compliance" element={<SelbstcheckComplianceResource />} />
              <Route path="/resources/hr-selbstcheck" element={<HrSelbstcheckResource />} />
              <Route path="/resources/entscheidungs-assistent" element={<EntscheidungsassistentResource />} />
              <Route path="/resources/frag-einen-expert" element={<AskExpertResource />} />
              <Route
                path="/resources/einladung-erstellen"
                element={canCreateInvites ? <InviteCreateResource /> : <Navigate to="/" replace />}
              />
              <Route
                path="/resources/hr-self-service-chatbot"
                element={<HrHandbookChatbotResource />}
              />
              <Route path="/resources/entgelttransparenz" element={<EntgelttransparenzResource />} />
              <Route path="/resources/ki-recruiting" element={<KiRecruitingResource />} />
            </Routes>
          </div>
        </main>
      </div>
    </div>
  );

  return resourcesShell;
}

// Schützt Routen: Login nötig, außer explizit öffentliche Tools (resourceConfig → requiresLogin: false).
function RequireAuth({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  const isPublicTool = isPublicResourcePath(location.pathname);

  /* Login vorübergehend umgangen: frontend/src/config/devBypass.js → DEV_SKIP_LOGIN */
  if (DEV_SKIP_LOGIN) {
    return children;
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#f1f5f9' }}>
        <p className="text-sm text-slate-400">Laden…</p>
      </div>
    );
  }

  if (!user && !isPublicTool) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <EntgeltEmbedProvider>
          <HandbookEmbedProvider>
            <DecisionEmbedProvider>
              <Routes>
                {/* Öffentliche Auth-Routen */}
                <Route path="/login" element={<LoginPage />} />
                <Route path="/einladung" element={<AcceptInvitePage />} />

                {/* Alle anderen Routen: Login erforderlich */}
                <Route
                  path="/*"
                  element={
                    <RequireAuth>
                      <AppContent />
                    </RequireAuth>
                  }
                />
              </Routes>
            </DecisionEmbedProvider>
          </HandbookEmbedProvider>
        </EntgeltEmbedProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
